import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  listNotifications,
  Notification,
  downloadNotificationReportPDF,
  downloadNotificationReportExcel,
} from '../../services/api';
import { getMaxDateToday, getMaxEndDateForStart } from '../../utils/dateUtils';

type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

const NOTIFICATION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'booking_created', label: 'Booking Created' },
  { value: 'booking_assigned', label: 'Booking Assigned' },
  { value: 'booking_reassigned', label: 'Booking Reassigned' },
  { value: 'booking_in_transit', label: 'Booking In Transit' },
  { value: 'booking_patient_pickedup', label: 'Patient Picked Up' },
  { value: 'booking_patient_dropped', label: 'Patient Dropped' },
  { value: 'booking_completed', label: 'Booking Completed' },
  { value: 'booking_cancelled', label: 'Booking Cancelled' },
  { value: 'booking_updated', label: 'Booking Updated' },
  { value: 'ambulance_assign', label: 'Ambulance Assign' },
  { value: 'ambulance_unassign', label: 'Ambulance Unassign' },
  { value: 'fuel_theft', label: 'Fuel Theft' },
  { value: 'fuel_refill', label: 'Fuel Refill' },
  { value: 'vehicle_halt', label: 'Vehicle Halt' },
  { value: 'system', label: 'System' },
];

const getTypeLabel = (type: string): string => {
  const found = NOTIFICATION_TYPES.find((t) => t.value === type);
  return found ? found.label : type;
};


const NotificationReport: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterType, customStartDate, customEndDate, typeFilter]);

  const getDateRange = (): { start: number; end: number } | null => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilterType) {
      case 'today': {
        const start = new Date(today);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'yesterday': {
        const start = new Date(today);
        start.setDate(start.getDate() - 1);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'custom': {
        if (!customStartDate || !customEndDate) return null;
        const maxEndStr = getMaxEndDateForStart(customStartDate);
        const endStr = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endStr);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      default:
        return null;
    }
  };

  const fetchData = async () => {
    const dateRange = getDateRange();
    if (!dateRange) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const res = await listNotifications(1, 5000, undefined, typeFilter || undefined, undefined, undefined);
      const all: Notification[] = res.data.data || [];
      // Client-side date filter since we pass timestamps
      const filtered = all.filter((n) => {
        const created = n.metadata.createdAt;
        return created >= dateRange.start && created <= dateRange.end;
      });
      setNotifications(filtered);
    } catch (err: any) {
      console.error('Failed to load notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = useMemo(() => {
    if (!searchTerm.trim()) return notifications;
    const s = searchTerm.toLowerCase();
    return notifications.filter(
      (n) =>
        n.spec.title?.toLowerCase().includes(s) ||
        n.spec.body?.toLowerCase().includes(s) ||
        getTypeLabel(n.spec.type).toLowerCase().includes(s)
    );
  }, [notifications, searchTerm]);

  const formatDateTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);

      const dateRange = getDateRange();
      if (!dateRange) {
        setDownloadError('Please select a valid date range');
        setDownloading(false);
        return;
      }

      let response;
      let fileExtension;

      if (format === 'pdf') {
        response = await downloadNotificationReportPDF(dateRange.start, dateRange.end, typeFilter || undefined, undefined);
        fileExtension = 'pdf';
      } else {
        response = await downloadNotificationReportExcel(dateRange.start, dateRange.end, typeFilter || undefined, undefined);
        fileExtension = 'xlsx';
      }

      const { url } = response.data.data;
      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr = dateFilterType === 'custom'
        ? `${customStartDate}_to_${customEndDate}`
        : dateFilterType;
      link.download = `notification-report-${dateRangeStr}.${fileExtension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case 'critical':
        return {
          backgroundColor: isDarkTheme ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
          color: isDarkTheme ? '#fca5a5' : '#dc2626',
          border: isDarkTheme ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #fee2e2',
        };
      case 'high':
        return {
          backgroundColor: isDarkTheme ? 'rgba(234, 88, 12, 0.15)' : '#fff7ed',
          color: isDarkTheme ? '#fdba74' : '#ea580c',
          border: isDarkTheme ? '1px solid rgba(234, 88, 12, 0.3)' : '1px solid #fed7aa',
        };
      case 'medium':
        return {
          backgroundColor: isDarkTheme ? 'rgba(202, 138, 4, 0.15)' : '#fefce8',
          color: isDarkTheme ? '#fde047' : '#ca8a04',
          border: isDarkTheme ? '1px solid rgba(202, 138, 4, 0.3)' : '1px solid #fef08a',
        };
      default:
        return {
          backgroundColor: isDarkTheme ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4',
          color: isDarkTheme ? '#86efac' : '#16a34a',
          border: isDarkTheme ? '1px solid rgba(22, 163, 74, 0.3)' : '1px solid #bbf7d0',
        };
    }
  };

  if (loading) {
    return <div><div className="loading">Loading notifications...</div></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: downloadError ? '10px' : '20px', flexWrap: 'wrap', gap: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
          <input
            type="text"
            placeholder="Search by title or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{
              padding: '8px 12px', borderRadius: '6px', fontSize: '16px', width: '220px',
              flex: '0 0 220px', border: '1px solid', borderColor: isDarkTheme ? '#334155' : '#ddd',
              height: '36px', boxSizing: 'border-box',
            }}
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="reports-filter-select"
            style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px', minWidth: '150px' }}
          >
            {NOTIFICATION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={dateFilterType}
            onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
            className="reports-filter-select"
            style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px', minWidth: '150px' }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Dates</option>
          </select>
          <div style={{ position: 'relative' }} ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={downloading || notifications.length === 0}
              style={{
                padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none',
                borderRadius: '6px', cursor: downloading || notifications.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '16px', fontWeight: '500',
                opacity: downloading || notifications.length === 0 ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              {downloading ? 'Generating...' : 'Download'}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            {showDownloadMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '5px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                borderRadius: '6px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, minWidth: '150px',
              }}>
                <button onClick={() => handleDownload('pdf')} disabled={downloading}
                  style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#ffffff' : '#333', border: 'none', borderRadius: '6px 6px 0 0', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '16px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >Download as PDF</button>
                <button onClick={() => handleDownload('excel')} disabled={downloading}
                  style={{ width: '100%', padding: '10px 15px', backgroundColor: 'transparent', color: isDarkTheme ? '#ffffff' : '#333', border: 'none', borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd', borderRadius: '0 0 6px 6px', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '16px', textAlign: 'left', opacity: downloading ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >Download as Excel</button>
              </div>
            )}
          </div>
        </div>
        {dateFilterType === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
              <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>Start Date:</label>
              <input
                type="date" value={customStartDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomStartDate(value);
                  if (value && customEndDate) {
                    const start = new Date(value);
                    const end = new Date(customEndDate);
                    const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                    if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                  }
                }}
                max={getMaxDateToday()} className="reports-date-input"
                style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px' }}
              />
              <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap' }}>End Date:</label>
              <input
                type="date" value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={customStartDate || undefined}
                max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateToday()} className="reports-date-input"
                style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '16px' }}
              />
            </div>
          )}
      </div>
      {downloadError && (
        <div style={{ padding: '10px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33', fontSize: '16px', marginBottom: '20px' }}>
          {downloadError}
        </div>
      )}
      {filteredNotifications.length === 0 ? (
        <div className="empty-state">
          <h3>{notifications.length === 0 ? 'No notifications found' : 'No matching results'}</h3>
          <p>{notifications.length === 0 ? 'No notifications for the selected period' : 'Try adjusting your search criteria'}</p>
        </div>
      ) : (
        <div
          className="table-container"
          style={{
            height: 'calc(100vh - 360px)', overflowY: 'auto', overflowX: 'auto',
            border: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
            backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
          }}
        >
          <table className="bookings-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '800px' }}>
            <thead>
              <tr>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  position: 'sticky', left: 0, zIndex: 10, whiteSpace: 'nowrap',
                }}>Date & Time</th>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}>Type</th>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}>Title</th>
                <th style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                }}>Message</th>
                <th style={{
                  padding: '8px 10px', textAlign: 'center', fontWeight: '600', fontSize: '15px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                  backgroundColor: isDarkTheme ? '#0f172a' : '#f8fafc',
                  borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}>Priority</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotifications.map((n, rowIdx) => {
                const baseBgColor = rowIdx % 2 === 0
                  ? (isDarkTheme ? '#1e293b' : '#ffffff')
                  : (isDarkTheme ? '#0f172a' : '#f8fafc');
                const hoverBgColor = isDarkTheme ? '#334155' : '#f1f5f9';
                const priorityStyle = getPriorityStyle(n.spec.priority);

                return (
                  <tr
                    key={n._id}
                    style={{ backgroundColor: baseBgColor, transition: 'background-color 0.2s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = hoverBgColor;
                      const first = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                      if (first) first.style.backgroundColor = hoverBgColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = baseBgColor;
                      const first = e.currentTarget.querySelector('td:first-child') as HTMLElement;
                      if (first) first.style.backgroundColor = baseBgColor;
                    }}
                  >
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      fontWeight: '500', fontSize: '14px',
                      position: 'sticky', left: 0, backgroundColor: baseBgColor, zIndex: 1,
                      whiteSpace: 'nowrap', transition: 'background-color 0.2s ease',
                    }}>
                      {formatDateTime(n.metadata.createdAt)}
                    </td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#cbd5e1' : '#475569', fontSize: '14px', whiteSpace: 'nowrap',
                    }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: '5px', fontSize: '13px', fontWeight: '500',
                        backgroundColor: isDarkTheme ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                        color: isDarkTheme ? '#60a5fa' : '#2563eb',
                      }}>
                        {getTypeLabel(n.spec.type)}
                      </span>
                    </td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                      fontWeight: '500', fontSize: '14px', maxWidth: '250px',
                    }}>
                      {n.spec.title}
                    </td>
                    <td style={{
                      padding: '8px 10px',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                      color: isDarkTheme ? '#cbd5e1' : '#475569', fontSize: '14px',
                      maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {n.spec.body}
                    </td>
                    <td style={{
                      padding: '8px 10px', textAlign: 'center',
                      borderBottom: isDarkTheme ? '1px solid #334155' : '1px solid #e2e8f0',
                    }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: '5px',
                        fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
                        ...priorityStyle,
                      }}>
                        {n.spec.priority}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationReport;
